import ContentSectionHeading from "components/company/sections/ContentSectionHeading";
import StaticContentSection from "components/company/StaticContentSection";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import useSelfie from "lib/hooks/useSelfie";
import { palette } from "lib/miolo";
import Link from "next/link";
import { useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";
import AmericanIssuerDividendsCollapsible from "./collapsible/AmericanIssuerDividendsCollapsible";
import AmericanIssuerExecutiveCompensationCollapsible from "./collapsible/AmericanIssuerExecutiveCompensationCollapsible";
import CorporateActionsCollapsible from "./collapsible/CorporateActionsCollapsible";
import AmericanOperatingSegmentsSection from "./content/AmericanOperatingSegmentsSection";
import { ContentSection } from "./ContentSection";
import ContentSectionBreadcrumbs from "./ContentSectionBreadcrumbs";

interface IAmericanCompanySectionsProps {
  tickerSymbol: string;
  company: IAmericanCompanyIssuerTopLevelFragment;
}

export default function AmericanCompanySections({ tickerSymbol, company }: IAmericanCompanySectionsProps) {
  const user = useSelfie();
  const [activeContentSections, setActiveContentSections] = useState<ContentSection[]>([]);

  const isContentSectionActive = useCallback(
    (contentSection: ContentSection) => activeContentSections.includes(contentSection),
    [activeContentSections],
  );

  const toggleActiveContentSection = useCallback(
    (nextContentSection: ContentSection) => {
      return () => {
        if (activeContentSections.includes(nextContentSection)) {
          setActiveContentSections(
            activeContentSections.filter((candidate) => {
              return candidate !== nextContentSection;
            }),
          );
        } else {
          setActiveContentSections(activeContentSections.concat(nextContentSection));
        }
      };
    },
    [activeContentSections],
  );

  if (!user) {
    return null;
  }

  return (
    <div className="flex w-screen flex-col items-center space-y-9 px-4 xl:mx-auto xl:max-w-7xl">
      <div className="w-full">
        <StaticContentSection primaryColor={palette.oceanGreen} css={{ rowGap: 12, alignItems: "center" }}>
          <div className="col-span-7 md:col-span-4">
            <ContentSectionHeading>Valuation</ContentSectionHeading>
            <ContentSectionBreadcrumbs segments={["Company Financials", "Valuation Indicators"]} />
          </div>

          <div className="col-span-1" />

          <div className="col-span-12 md:col-span-7">
            <div className="flex space-x-4">
              {company.securities.map((security) => {
                const linkHref = `/us/${security.tickerSymbol.toLowerCase()}/valuation`;
                const linkAs = `/[country]/[tickerSymbol]/valuation`;

                return (
                  <Link
                    key={security.tickerSymbol}
                    as={linkHref}
                    href={linkAs}
                    className={twMerge(
                      "flex rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800",
                      "border border-transparent",
                      "hover:border-emerald-600",
                    )}
                  >
                    {security.tickerSymbol}
                  </Link>
                );
              })}
            </div>
          </div>
        </StaticContentSection>
      </div>

      {user.isPremium && (
        <AmericanIssuerExecutiveCompensationCollapsible
          tickerSymbol={tickerSymbol}
          isREIT={company.isREIT}
          isExpanded={isContentSectionActive(ContentSection.EXECUTIVE_COMPENSATION)}
          onToggleCollapsed={toggleActiveContentSection(ContentSection.EXECUTIVE_COMPENSATION)}
        />
      )}

      {user.isPremium && (
        <CorporateActionsCollapsible
          tickerSymbols={[tickerSymbol]}
          isExpanded={isContentSectionActive(ContentSection.CORPORATE_ACTIONS)}
          onToggleCollapsed={toggleActiveContentSection(ContentSection.CORPORATE_ACTIONS)}
        />
      )}

      {user.isPremium && <AmericanOperatingSegmentsSection tickerSymbol={tickerSymbol} />}

      {user.isPremium && (
        <AmericanIssuerDividendsCollapsible
          tickerSymbol={tickerSymbol}
          isExpanded={isContentSectionActive(ContentSection.DIVIDENDS)}
          onToggleCollapsed={toggleActiveContentSection(ContentSection.DIVIDENDS)}
        />
      )}
    </div>
  );
}
